import React, { useEffect, useState } from "react";

const DEFAULT_SLOW_RESPONSE_DELAY_MS = 3000;

interface ChatTypingIndicatorProps {
  /** Shown in place of the dots once the response has been pending for the delay below. */
  slowResponseNotice?: string;
  slowResponseNoticeDelayMs?: number;
  ariaLabel?: string;
}

/**
 * Animated typing dots (or the slow-response notice) for inside a chat bubble.
 * Mount it only while a response is pending — mounting starts the slow-response
 * clock and unmounting resets it for the next message.
 */
const ChatTypingIndicator = ({
  slowResponseNotice,
  slowResponseNoticeDelayMs = DEFAULT_SLOW_RESPONSE_DELAY_MS,
  ariaLabel = "Assistant is thinking",
}: ChatTypingIndicatorProps) => {
  const [isSlow, setIsSlow] = useState(false);

  useEffect(() => {
    if (!slowResponseNotice) {
      return;
    }
    const timer = window.setTimeout(() => setIsSlow(true), slowResponseNoticeDelayMs);
    return () => window.clearTimeout(timer);
  }, [slowResponseNotice, slowResponseNoticeDelayMs]);

  if (slowResponseNotice && isSlow) {
    return (
      <p className="text-[13px] leading-5 text-neutral-500" aria-label={ariaLabel}>
        {slowResponseNotice}
      </p>
    );
  }

  return (
    <span className="inline-flex gap-1 py-1.5" aria-label={ariaLabel}>
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-neutral-400" />
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-neutral-400 [animation-delay:150ms]" />
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-neutral-400 [animation-delay:300ms]" />
    </span>
  );
};

export default ChatTypingIndicator;
